import { DestroyRef, inject, type Signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { TimesheetPriorityApiService } from '@app/+authenticated/+timesheet/shared/priority/timesheet-priority-api.service';
import {
  isPriorityFromApiEnabled,
  loadPriorityEntities,
  loadRangeConflicts,
  type PriorityRequest,
  type TimesheetPriorityDeps,
} from '@app/+authenticated/+timesheet/shared/priority/timesheet-priority.loader';
import { TimesheetHelperService } from '@app/+authenticated/+timesheet/shared/timesheet-helper.service';
import { Actions, ofType } from '@ngrx/effects';
import { timesheetActionType } from '@reducers/orm/timesheet/timesheet.action';
import { FeatureService } from '@sb/shared/feature';
import { plainDateRange, toISODateString } from '@shiftbase-com/ts-shared/utilities';
import { catchError, EMPTY, forkJoin, map, of, switchMap, take, tap } from 'rxjs';

import { PayrollRunService } from './payroll-run.service';
import { type PayrollRunScope } from './payroll.model';

/**
 * Warms the run's timesheets and, when the priority endpoint is in play, the categorization the
 * list view reads from it — so the progress panel's outstanding count and the timesheets step
 * bucket a row identically instead of the panel counting by the local rules and the step by the
 * server's.
 *
 * Must be called from an injection context; it follows the scope for the rest of the flow's life
 * and its `takeUntilDestroyed` ends every request when the run host goes away.
 */
export function usePayrollTimesheets(scope: Signal<PayrollRunScope | null>): void {
  const deps: TimesheetPriorityDeps = {
    priorityApi: inject(TimesheetPriorityApiService),
    timesheetHelper: inject(TimesheetHelperService),
  };
  const featureService = inject(FeatureService);
  const run = inject(PayrollRunService);
  const actions = inject(Actions);
  const destroyRef = inject(DestroyRef);

  // Held here rather than on the run service so a failed response, which leaves the entities
  // `null`, still leaves a request to retry after the next mutation.
  let lastPriorityRequest: PriorityRequest | null = null;

  // `null` is mapped here rather than filtered out upstream: a `filter` before the `switchMap`
  // would leave the request running after `reset()`, since `switchMap` only tears down its inner
  // subscription when it receives a new value. Load state is `reset()`'s own business, so a
  // teardown contributes no report of its own.
  toObservable(scope)
    .pipe(
      switchMap((currentScope) => {
        if (currentScope === null) {
          lastPriorityRequest = null;

          return EMPTY;
        }

        lastPriorityRequest = priorityRequestFor(currentScope, featureService);

        return loadScope(deps, run, currentScope, lastPriorityRequest);
      }),
      takeUntilDestroyed(destroyRef),
    )
    .subscribe((failed) => run.onTimesheetsSettled(failed));

  // The store's own reducers already fold a mutation into the rows, but the priority map is a
  // snapshot the endpoint has to answer for again.
  actions
    .pipe(
      ofType(
        timesheetActionType.ADD_SUCCESS,
        timesheetActionType.UPDATE_SUCCESS,
        timesheetActionType.BATCH_APPROVAL_SUCCESS,
        timesheetActionType.CLOCK_SAVE_SUCCESS,
      ),
      switchMap(() => {
        const request = lastPriorityRequest;

        if (!request) {
          return of(null);
        }

        return fetchPriority(deps, run, request).pipe(
          switchMap(() => backfillOmittedConflicts(deps, run, request)),
          catchError(() => of(null)),
        );
      }),
      takeUntilDestroyed(destroyRef),
    )
    .subscribe();
}

function priorityRequestFor(scope: PayrollRunScope, featureService: FeatureService): PriorityRequest | null {
  if (!isPriorityFromApiEnabled(featureService, scope.departmentIds)) {
    return null;
  }

  return {
    range: plainDateRange(toISODateString(scope.range.start), toISODateString(scope.range.end)),
    departmentIds: scope.departmentIds,
  };
}

function loadScope(
  deps: TimesheetPriorityDeps,
  run: PayrollRunService,
  scope: PayrollRunScope,
  request: PriorityRequest | null,
) {
  if (!request) {
    run.onPriorityLoaded(null);
  }

  // A request of their own, so a schedule or conflict failure cannot cancel them: the lock count
  // reads the day logs and has no failure state to fall back on.
  const timesheetsAndDaylogs = deps.timesheetHelper
    .loadOpenCloseData(scope.range.start, scope.range.end, scope.departmentIds)
    .pipe(
      take(1),
      map(() => false),
      catchError(() => of(true)),
    );

  const schedulesAndConflicts = forkJoin([
    deps.timesheetHelper
      .loadDataObs(scope.range.start, scope.range.end, undefined, scope.departmentIds, {
        conflicts: !request,
        includeLoanedShifts: false,
        // `PayrollAbsencesStore` and `usePayrollScopeData` already request these for the same
        // period, and they track the load state their steps wait on.
        absences: false,
        contracts: false,
        timesheets: false,
        daylogs: false,
      })
      .pipe(take(1)),
    request ? fetchPriority(deps, run, request) : of(null),
  ]).pipe(
    map(() => false),
    catchError(() => of(true)),
  );

  return forkJoin([timesheetsAndDaylogs, schedulesAndConflicts]).pipe(
    switchMap(([timesheetsFailed, schedulesFailed]) => {
      const failed = timesheetsFailed || schedulesFailed;

      // After the rows are in the store, so the omitted-row check has something to compare against.
      return (request && !failed ? backfillOmittedConflicts(deps, run, request) : of(null)).pipe(map(() => failed));
    }),
    catchError(() => of(true)),
  );
}

/**
 * A priority failure is not a timesheets failure: the rows themselves are loaded, so the run falls
 * back to categorizing them locally rather than reporting the step unmeasurable — which is why
 * `loadPriorityEntities`' own `null` result is passed straight through. Shift rows are dropped:
 * payroll measures outstanding timesheets, and a shift with no timesheet is not one.
 */
function fetchPriority(deps: TimesheetPriorityDeps, run: PayrollRunService, request: PriorityRequest) {
  return loadPriorityEntities(deps, request.range, request.departmentIds).pipe(
    tap((entities) => run.onPriorityLoaded(entities?.timesheets ?? null)),
  );
}

function backfillOmittedConflicts(deps: TimesheetPriorityDeps, run: PayrollRunService, request: PriorityRequest) {
  return run.priorityOmitsLoadedRow() ? loadRangeConflicts(deps, request.range, request.departmentIds) : of(null);
}
